import { useState, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { dataApi } from '../services/apiService'
import toast from 'react-hot-toast'
import { RevoGrid } from '@revolist/react-datagrid'
import '@revolist/revogrid-pro/dist/revogrid-pro.css'
import { ExportExcelPlugin } from '@revolist/revogrid-pro'

// Data History Component with RevoGrid integration
const DataHistory = () => {
  const { t } = useTranslation()
  const [sessions, setSessions] = useState([])
  const [selectedSession, setSelectedSession] = useState(null)
  const [sessionResults, setSessionResults] = useState([])
  const [loading, setLoading] = useState(true)
  const [showDetails, setShowDetails] = useState(false)
  const [viewMode, setViewMode] = useState('sessions')
  const [allResults, setAllResults] = useState([])
  const [detailViewMode, setDetailViewMode] = useState('table') // 'table', 'cards', or 'stacked'
  const [selectedResult, setSelectedResult] = useState(null) // For card detail modal
  const [showResultDetail, setShowResultDetail] = useState(false)

  useEffect(() => {
    fetchHistory()
  }, [])

  const fetchHistory = async () => {
    try {
      setLoading(true)
      const response = await dataApi.getHistory()
      setSessions(response.items || response.sessions || [])
      
      const allResultsArray = []
      if (response.items && response.items.length > 0) {
        for (const session of response.items.slice(0, 5)) {
          try {
            const resultsData = await dataApi.getSessionResults(session.session_id)
            if (resultsData.results) {
              allResultsArray.push(...resultsData.results)
            }
          } catch (e) {
            console.error('Failed to fetch results for session:', e)
          }
        }
      }
      setAllResults(allResultsArray)
      
    } catch (error) {
      console.error('Failed to fetch history:', error)
      toast.error(t('history.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const viewSessionDetails = async (sessionId) => {
    try {
      const [sessionData, resultsData] = await Promise.all([
        dataApi.getSession(sessionId),
        dataApi.getSessionResults(sessionId)
      ])
      
      setSelectedSession(sessionData)
      setSessionResults(resultsData.results || [])
      setShowDetails(true)
    } catch (error) {
      console.error('Failed to fetch session details:', error)
      toast.error(t('history.loadDetailsFailed'))
    }
  }

  const deleteSession = async (sessionId) => {
    if (!window.confirm(t('history.deleteConfirm'))) return
    
    try {
      await dataApi.deleteSession(sessionId)
      toast.success(t('history.sessionDeleted'))
      fetchHistory()
      if (selectedSession?.session_id === sessionId) {
        setShowDetails(false)
        setSelectedSession(null)
      }
    } catch (error) {
      toast.error('Failed to delete session')
    }
  }

  // Cleanup function removed - use backend scripts for cleanup instead
  // const cleanupOldSessions = async () => {
  //   if (!window.confirm('Delete all sessions older than 30 days?')) return
  //   
  //   try {
  //     const response = await dataApi.cleanupSessions(30)
  //     toast.success(`Deleted ${response.deleted_count} old sessions`)
  //     fetchHistory()
  //   } catch (error) {
  //     toast.error('Cleanup failed')
  //   }
  // }

  const getAllFields = (results) => {
    const fields = new Set()
    results.forEach(result => {
      if (result.extracted_data) {
        Object.keys(result.extracted_data).forEach(key => {
          if (!['source_url', 'date_collected', 'date_entered', 'status'].includes(key)) {
            fields.add(key)
          }
        })
      }
    })
    return Array.from(fields)
  }

  const formatFieldName = (fieldName) => {
    return fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const groupFieldsByPrefix = (results) => {
    const fields = getAllFields(results)
    const groups = {}
    
    fields.forEach(field => {
      // Extract prefix (everything before first underscore)
      const parts = field.split('_')
      const prefix = parts.length > 1 ? parts[0] : 'general'
      
      if (!groups[prefix]) {
        groups[prefix] = []
      }
      groups[prefix].push(field)
    })
    
    return groups
  }

  const calculateScore = (extractedData) => {
    if (!extractedData) return 0
    
    const allFields = Object.keys(extractedData).filter(key => 
      !['source_url', 'date_collected', 'date_entered', 'status'].includes(key)
    )
    
    if (allFields.length === 0) return 0
    
    let filledCount = 0
    allFields.forEach(field => {
      const value = extractedData[field]
      if (value && value !== '' && value !== '-') {
        filledCount++
      }
    })
    
    return Math.round((filledCount / allFields.length) * 100)
  }

  const getScoreColor = (score) => {
    return 'text-green-600 bg-green-50'
  }

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
  }

  const generateCSV = (results) => {
    if (results.length === 0) return ''
    
    const fields = getAllFields(results)
    const headers = [...fields, 'source_url', 'date', 'status']
    const headerRow = headers.join(',')
    
    const dataRows = results.map(result => {
      const row = fields.map(field => {
        const value = result.extracted_data?.[field] || ''
        return escapeCSV(value)
      })
      row.push(escapeCSV(result.url || ''))
      row.push(escapeCSV(result.created_at ? new Date(result.created_at).toLocaleString() : ''))
      row.push(result.success ? 'success' : 'failed')
      return row.join(',')
    })
    
    return [headerRow, ...dataRows].join('\n')
  }

  const escapeCSV = (value) => {
    const str = String(value)
    return str.includes(',') || str.includes('"') || str.includes('\n')
      ? `"${str.replace(/"/g, '""')}"` 
      : str
  }

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-12 w-12">
            <i className="fa-solid fa-spinner text-primary-500 text-4xl"></i>
          </div>
          <span className="ml-3 text-dark-100 font-medium">{t('history.loading')}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card border-l-4 border-l-primary-500">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-2xl font-bold text-dark-300">{t('history.title')}</h2>
              <p className="text-dark-100">
                {sessions.length === 1 ? t('history.subtitle', { sessions: sessions.length, results: allResults.length }) : t('history.subtitlePlural', { sessions: sessions.length, results: allResults.length })}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            {/* View Mode Switcher */}
            <div className="flex bg-light-100 rounded-lg p-1 border border-light-200/30">
              <button
                onClick={() => setViewMode('sessions')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  viewMode === 'sessions' 
                    ? 'bg-white text-primary-600 shadow-md' 
                    : 'text-dark-100 hover:text-dark-300'
                }`}
              >
                <i className="fa-solid fa-folder mr-2"></i>
                {t('history.viewMode.sessions')} ({sessions.length})
              </button>
              <button
                onClick={() => setViewMode('all_results')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  viewMode === 'all_results' 
                    ? 'bg-white text-primary-600 shadow-md' 
                    : 'text-dark-100 hover:text-dark-300'
                }`}
              >
                <i className="fa-solid fa-table mr-2"></i>
                {t('history.viewMode.allResults')} ({allResults.length})
              </button>
            </div>
            <button
              onClick={fetchHistory}
              className="px-4 py-2 bg-white border-2 border-light-200/30 hover:border-primary-500 hover:bg-light-100 text-dark-300 rounded-lg font-semibold transition-all"
            >
              <i className="fa-solid fa-rotate mr-2"></i>
              {t('common.refresh')}
            </button>
            {/* Cleanup Old button removed - use backend scripts for cleanup instead */}
          </div>
        </div>

        {/* Export Buttons */}
        {(viewMode === 'sessions' && sessions.length > 0) || (viewMode === 'all_results' && allResults.length > 0) ? (
          <div className="flex gap-3 mb-4 pb-4 border-b border-light-200/20">
            <button
              onClick={() => {
                const csv = viewMode === 'sessions' ? generateCSV(allResults) : generateCSV(allResults)
                downloadFile(csv, `data_${viewMode}_${Date.now()}.csv`, 'text/csv')
              }}
              className="px-4 py-2 bg-white border-2 border-light-200/30 hover:border-primary-500 hover:bg-light-100 text-dark-300 rounded-lg font-semibold transition-all"
            >
              <i className="fa-solid fa-download mr-2"></i>
              {t('history.export.csv')}
            </button>
            <button
              onClick={() => {
                const json = JSON.stringify(viewMode === 'sessions' ? sessions : allResults.map(r => r.extracted_data), null, 2)
                downloadFile(json, `data_${viewMode}_${Date.now()}.json`, 'application/json')
              }}
              className="px-4 py-2 bg-white border-2 border-light-200/30 hover:border-primary-500 hover:bg-light-100 text-dark-300 rounded-lg font-semibold transition-all"
            >
              <i className="fa-solid fa-download mr-2"></i>
              {t('history.export.json')}
            </button>
          </div>
        ) : null}

        {/* View: Sessions List */}
        {viewMode === 'sessions' && sessions.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-light-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fa-solid fa-clock-rotate-left text-light-200 text-4xl"></i>
            </div>
            <h3 className="text-xl font-semibold text-dark-300 mb-2">{t('history.empty.noHistory')}</h3>
            <p className="text-dark-100">{t('history.empty.noHistoryDesc')}</p>
          </div>
        ) : viewMode === 'sessions' ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gradient-to-r from-light-100 to-light-100/50">
                <tr className="text-left text-dark-100">
                  <th className="px-4 py-3 font-bold">{t('history.table.searchQuery')}</th>
                  <th className="px-4 py-3 font-bold">{t('history.table.date')}</th>
                  <th className="px-4 py-3 text-right font-bold">{t('history.table.urls')}</th>
                  <th className="px-4 py-3 text-right font-bold">{t('history.table.successful')}</th>
                  <th className="px-4 py-3 text-right font-bold">{t('history.table.failed')}</th>
                  <th className="px-4 py-3 font-bold">{t('history.table.status')}</th>
                  <th className="px-4 py-3 font-bold">{t('history.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session, idx) => (
                  <tr key={idx} className="border-t border-light-200/30 hover:bg-light-100/50 transition-colors">
                    <td className="px-4 py-3 font-semibold text-dark-300">{session.search_query || 'N/A'}</td>
                    <td className="px-4 py-3 text-dark-100">
                      {new Date(session.started_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-dark-300">{session.total_urls || 0}</td>
                    <td className="px-4 py-3 text-right text-green-600 font-bold">
                      {session.successful_extractions || 0}
                    </td>
                    <td className="px-4 py-3 text-right text-red-600 font-bold">
                      {session.failed_extractions || 0}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        session.status === 'completed' ? 'bg-green-100 text-green-800' :
                        session.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                        session.status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {t('history.status.' + session.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => viewSessionDetails(session.session_id)}
                          className="px-3 py-1 bg-white border border-light-200/30 hover:border-primary-500 hover:bg-light-100 text-dark-300 rounded-lg font-semibold text-sm transition-all"
                        >
                          <i className="fa-solid fa-eye mr-1"></i>
                          {t('history.table.view')}
                        </button>
                        <button
                          onClick={() => deleteSession(session.session_id)}
                          className="px-3 py-1 bg-white border border-light-200/30 hover:border-red-500 hover:bg-red-50 text-dark-300 hover:text-red-600 rounded-lg font-semibold text-sm transition-all"
                        >
                          <i className="fa-solid fa-trash mr-1"></i>
                          {t('history.table.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* View: All Results */
          <div>
            <div className="mb-4 p-3 bg-primary-50 border-l-4 border-primary-500 rounded-r-lg">
              <p className="text-sm text-dark-300 font-medium">
                <i className="fa-solid fa-info-circle text-primary-500 mr-2"></i>
                {t('history.allResults.showing')}
              </p>
            </div>
            {allResults.length === 0 ? (
              <div className="text-center py-12 text-dark-100">
                <i className="fa-solid fa-inbox text-light-200 text-5xl mb-3"></i>
                <p className="font-medium">{t('history.allResults.noResults')}</p>
              </div>
            ) : (
              <AllResultsGrid results={allResults} />
            )}
          </div>
        )}
      </div>

      {/* Session Details Modal */}
      {showDetails && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-7xl w-full h-[95vh] overflow-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-light-200/20 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-dark-300">
                    Session Details
                  </h3>
                  <p className="text-sm text-dark-100 mt-1">
                    {selectedSession.search_query} • {new Date(selectedSession.started_at).toLocaleString()}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-xs">
                    <span className="text-dark-300">
                      <span className="font-semibold">Total:</span> {selectedSession.total_urls}
                    </span>
                    <span className="text-green-600">
                      <span className="font-semibold">✓ Successful:</span> {selectedSession.successful_extractions}
                    </span>
                    <span className="text-red-600">
                      <span className="font-semibold">✗ Failed:</span> {selectedSession.failed_extractions}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full font-semibold ${
                      selectedSession.status === 'completed' ? 'bg-green-100 text-green-800' :
                      selectedSession.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                      selectedSession.status === 'failed' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedSession.status}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-dark-100 hover:text-dark-300 transition-colors"
                >
                  <i className="fa-solid fa-xmark text-3xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">

              {/* Export Links */}
              <div className="mb-6 p-4 bg-gradient-to-r from-primary-dark to-primary-500 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="text-white">
                    <div className="font-bold text-lg mb-1">
                      <i className="fas fa-share-from-square mr-2"></i>
                      Public Export Links
                    </div>
                    <div className="text-sm text-white/80">Share session data without authentication</div>
                  </div>
                  <div className="flex space-x-3">
                    <a
                      href={`/api/export/session/${selectedSession.session_id}.json`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-6 py-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white rounded-lg font-semibold transition-all flex items-center"
                    >
                      <i className="fas fa-file-code mr-2"></i>
                      JSON
                    </a>
                    <a
                      href={`/api/export/session/${selectedSession.session_id}.html`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-6 py-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white rounded-lg font-semibold transition-all flex items-center"
                    >
                      <i className="fas fa-file-lines mr-2"></i>
                      HTML
                    </a>
                  </div>
                </div>
              </div>

              {/* Results Section with View Switcher */}
              {sessionResults.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-bold text-dark-300 flex items-center">
                      <i className="fa-solid fa-list text-primary-500 mr-2"></i>
                      Results ({sessionResults.length})
                    </h4>
                    {/* View Mode Toggle */}
                    <div className="flex bg-light-100 rounded-lg p-1 border border-light-200/30">
                      <button
                        onClick={() => setDetailViewMode('table')}
                        className={`px-3 py-1 rounded-lg text-sm font-semibold transition-all ${
                          detailViewMode === 'table' 
                            ? 'bg-white text-primary-600 shadow-md' 
                            : 'text-dark-100 hover:text-dark-300'
                        }`}
                      >
                        <i className="fa-solid fa-table mr-1"></i>
                        Table
                      </button>
                      <button
                        onClick={() => setDetailViewMode('stacked')}
                        className={`px-3 py-1 rounded-lg text-sm font-semibold transition-all ${
                          detailViewMode === 'stacked' 
                            ? 'bg-white text-primary-600 shadow-md' 
                            : 'text-dark-100 hover:text-dark-300'
                        }`}
                      >
                        <i className="fa-solid fa-layer-group mr-1"></i>
                        Stacked
                      </button>
                      <button
                        onClick={() => setDetailViewMode('cards')}
                        className={`px-3 py-1 rounded-lg text-sm font-semibold transition-all ${
                          detailViewMode === 'cards' 
                            ? 'bg-white text-primary-600 shadow-md' 
                            : 'text-dark-100 hover:text-dark-300'
                        }`}
                      >
                        <i className="fa-solid fa-grip mr-1"></i>
                        Cards
                      </button>
                    </div>
                  </div>

                  {/* Table View */}
                  {detailViewMode === 'table' && (
                    <div className="max-h-[70vh]">
                      <SessionResultsGrid results={sessionResults} />
                    </div>
                  )}

                  {/* Stacked View */}
                  {detailViewMode === 'stacked' && (
                    <div className="space-y-6 max-h-[70vh] overflow-y-auto">
                      {Object.entries(groupFieldsByPrefix(sessionResults)).map(([prefix, fields]) => (
                        <div key={prefix} className="border border-light-200/30 rounded-lg overflow-hidden">
                          {/* Group Header */}
                          <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-4 py-2">
                            <h5 className="text-white font-bold text-sm uppercase flex items-center">
                              <i className="fa-solid fa-folder mr-2"></i>
                              {formatFieldName(prefix)} ({fields.length} fields)
                            </h5>
                          </div>
                          
                          {/* Group Table with RevoGrid */}
                          <div style={{ height: '400px' }}>
                            <StackedGroupGrid 
                              results={sessionResults} 
                              fields={fields} 
                              formatFieldName={formatFieldName}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Card View */}
                  {detailViewMode === 'cards' && (
                    <div className="space-y-2 max-h-[70vh] overflow-y-auto">
                      {sessionResults.map((result, idx) => {
                        // Get first available non-empty field value for display
                        const getCardTitle = () => {
                          if (!result.extracted_data) return 'Extraction Result'
                          const meta = ['source_url', 'date_collected', 'date_entered', 'status']
                          const firstValue = Object.entries(result.extracted_data)
                            .filter(([k]) => !meta.includes(k))
                            .find(([, v]) => v != null && v !== '' && v !== '-')
                          return firstValue ? firstValue[1] : 'Extraction Result'
                        }
                        
                        return (
                          <div 
                            key={idx} 
                            onClick={() => {
                              setSelectedResult(result)
                              setShowResultDetail(true)
                            }}
                            className="p-4 bg-light-100 rounded-lg hover:shadow-md transition-all border border-light-200/30 cursor-pointer hover:bg-light-200/50"
                          >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                                <p className="font-semibold text-dark-300 mb-1">{getCardTitle()}</p>
                              <a 
                                href={result.url}
                                target="_blank"
                                rel="noopener noreferrer" 
                                className="text-xs text-primary-600 hover:underline break-all"
                                  onClick={(e) => e.stopPropagation()}
                              >
                                {result.url}
                              </a>
                            </div>
                            <div className="ml-4">
                              {result.success ? (
                                <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold">
                                  <i className="fa-solid fa-check-circle mr-1"></i>
                                  Success
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-bold" title={result.error}>
                                  <i className="fa-solid fa-times-circle mr-1"></i>
                                  Failed
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>
      )}

      {/* Result Detail Modal */}
      {showResultDetail && selectedResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-light-200/20 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-dark-300">
                    Result Details
                  </h3>
                  <p className="text-sm text-dark-100 mt-1">
                    {selectedResult.created_at ? new Date(selectedResult.created_at).toLocaleString() : 'No date'}
                  </p>
                </div>
                <button
                  onClick={() => setShowResultDetail(false)}
                  className="text-dark-100 hover:text-dark-300 transition-colors"
                >
                  <i className="fa-solid fa-xmark text-3xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Source URL */}
              <div className="mb-6 p-4 bg-gradient-to-r from-primary-dark to-primary-500 rounded-xl">
                <div className="text-white">
                  <div className="font-semibold text-xs mb-1 opacity-80">SOURCE URL</div>
                  <a 
                    href={selectedResult.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white hover:underline break-all text-sm"
                  >
                    {selectedResult.url}
                  </a>
                </div>
              </div>

              {/* Status Badge */}
              <div className="mb-6">
                {selectedResult.success ? (
                  <span className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm font-bold">
                    <i className="fa-solid fa-check-circle mr-2"></i>
                    Extraction Successful
                  </span>
                ) : (
                  <div>
                    <span className="inline-flex items-center px-4 py-2 bg-red-100 text-red-800 rounded-lg text-sm font-bold mb-2">
                      <i className="fa-solid fa-times-circle mr-2"></i>
                      Extraction Failed
                    </span>
                    {selectedResult.error_message && (
                      <p className="text-xs text-red-600 mt-2">{selectedResult.error_message}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Extracted Data - always show section; explain when empty */}
              <div>
                <h4 className="text-lg font-bold text-dark-300 mb-4 flex items-center">
                  <i className="fa-solid fa-database text-primary-500 mr-2"></i>
                  Extracted Data
                </h4>
                {selectedResult.extracted_data && Object.keys(selectedResult.extracted_data).length > 0 ? (
                  <div className="grid grid-cols-1 gap-3">
                    {Object.entries(selectedResult.extracted_data).map(([key, value]) => {
                      if (!value || value === '-') return null
                      
                      return (
                        <div key={key} className="p-4 bg-light-100 rounded-lg border border-light-200/30">
                          <div className="text-xs text-dark-100 font-semibold mb-1 uppercase">
                            {formatFieldName(key)}
                          </div>
                          <div className="text-sm text-dark-300">
                            {key === 'telephone' && value !== '-' ? (
                              <a href={`tel:${value}`} className="text-primary-600 hover:text-primary-800 font-medium">
                                <i className="fa-solid fa-phone mr-2"></i>
                                {value}
                              </a>
                            ) : key === 'email' && value !== '-' ? (
                              <a href={`mailto:${value}`} className="text-primary-600 hover:text-primary-800 font-medium">
                                <i className="fa-solid fa-envelope mr-2"></i>
                                {value}
                              </a>
                            ) : (key === 'website' || key.includes('url')) && value !== '-' ? (
                              <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                <i className="fa-solid fa-link mr-2"></i>
                                {value}
                              </a>
                            ) : (
                              value
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                    <p className="font-medium mb-1">{t('history.noExtractedDataTitle')}</p>
                    <p className="text-amber-700">
                      {t('history.noExtractedDataDesc')}
                    </p>
                    {selectedResult.error_message && (
                      <p className="mt-2 text-xs font-mono bg-amber-100/50 p-2 rounded border border-amber-200">
                        {selectedResult.error_message}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Grid component for All Results view
const AllResultsGrid = ({ results }) => {
  const gridRef = useRef(null)

  const handleExportCSV = async () => {
    if (!gridRef.current) return
    
    try {
      const plugins = await gridRef.current.getPlugins()
      const exportPlugin = plugins.find(p => p.exportFile)
      
      if (exportPlugin) {
        await exportPlugin.exportFile({ 
          filename: `data_all_results_${Date.now()}`,
          mime: 'text/csv',
          fileKind: 'csv'
        })
        toast.success('CSV export successful!')
      }
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Export failed')
    }
  }

  const handleExportExcel = async () => {
    if (!gridRef.current) return
    
    try {
      const plugins = await gridRef.current.getPlugins()
      const exportPlugin = plugins.find(p => p instanceof ExportExcelPlugin)
      
      if (exportPlugin) {
        const exportConfig = {
          sheetName: `data_all_results_${Date.now()}.xlsx`,
          writingOptions: {
            type: 'file',
            bookType: 'xlsx',
            compression: true,
            ignoreEC: true
          }
        }
        await exportPlugin.export(exportConfig)
        toast.success('Excel export successful!')
      } else {
        toast.error('Excel export plugin not available')
      }
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Export failed')
    }
  }

  const getAllFields = (results) => {
    const fields = new Set()
    results.forEach(result => {
      if (result.extracted_data) {
        Object.keys(result.extracted_data).forEach(key => {
          if (!['source_url', 'date_collected', 'date_entered', 'status'].includes(key)) {
            fields.add(key)
          }
        })
      }
    })
    return Array.from(fields)
  }

  const formatFieldName = (fieldName) => {
    return fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const calculateScore = (extractedData) => {
    if (!extractedData) return 0
    
    const allFields = Object.keys(extractedData).filter(key => 
      !['source_url', 'date_collected', 'date_entered', 'status'].includes(key)
    )
    
    if (allFields.length === 0) return 0
    
    let filledCount = 0
    allFields.forEach(field => {
      const value = extractedData[field]
      if (value && value !== '' && value !== '-') {
        filledCount++
      }
    })
    
    return Math.round((filledCount / allFields.length) * 100)
  }

  const columns = useMemo(() => {
    const fields = getAllFields(results)
    
    return [
      {
        prop: 'status',
        name: 'Status',
        size: 100,
        sortable: true,
        cellTemplate: (h, { model }) => {
          const status = model.success
          return h('div', {
            class: 'flex items-center justify-center',
            innerHTML: status 
              ? '<i class="fa-solid fa-check-circle text-green-600"></i>' 
              : '<i class="fa-solid fa-times-circle text-red-600"></i>'
          })
        }
      },
      {
        prop: 'source_url',
        name: 'Source URL',
        size: 120,
        sortable: true,
        cellTemplate: (h, { model }) => {
          return h('a', {
            href: model.url,
            target: '_blank',
            rel: 'noopener noreferrer',
            class: 'text-primary-600 hover:text-primary-800',
            innerHTML: '<i class="fa-solid fa-link"></i>'
          })
        }
      },
      ...fields.map(field => ({
        prop: field,
        name: formatFieldName(field),
        size: 200,
        sortable: true,
        cellTemplate: (h, { model }) => {
          const value = model[field] || '-'
          
          if (field === 'telephone' && value !== '-') {
            return h('a', {
              href: `tel:${value}`,
              class: 'text-primary-600 hover:text-primary-800 font-medium',
              innerHTML: value
            })
          }
          if (field === 'email' && value !== '-') {
            return h('a', {
              href: `mailto:${value}`,
              class: 'text-primary-600 hover:text-primary-800 font-medium',
              innerHTML: value
            })
          }
          
          return h('span', {
            class: 'text-dark-300',
            innerHTML: value
          })
        }
      })),
      {
        prop: 'score',
        name: 'Score',
        size: 120,
        sortable: true,
        order: 'desc',
        cellTemplate: (h, { model }) => {
          const score = model.score
          return h('div', {
            class: 'flex items-center gap-2',
            innerHTML: `<span class="px-2 py-1 rounded-full font-bold text-sm text-green-600 bg-green-50">${score}</span>`
          })
        }
      },
      {
        prop: 'date',
        name: 'Date',
        size: 180,
        sortable: true
      }
    ]
  }, [results])

  const source = useMemo(() => {
    return results.map(result => {
      const row = {
        success: result.success,
        score: calculateScore(result.extracted_data),
        date: result.created_at ? new Date(result.created_at).toLocaleString() : '-',
        url: result.url,
        source_url: result.url,
        ...result.extracted_data
      }
      return row
    })
  }, [results])

  return (
    <div>
      <div className="mb-4 flex gap-3">
                <button
          onClick={handleExportCSV}
          className="px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-lg font-semibold transition-all shadow-md hover:shadow-lg flex items-center"
        >
          <i className="fa-solid fa-file-csv mr-2"></i>
                  Export CSV
                </button>
                <button
          onClick={handleExportExcel}
          className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg font-semibold transition-all shadow-md hover:shadow-lg flex items-center"
        >
          <i className="fa-solid fa-file-excel mr-2"></i>
          Export Excel
                </button>
              </div>
      <div style={{ height: '500px' }}>
        <RevoGrid 
          ref={gridRef}
          columns={columns} 
          source={source}
          theme="compact"
          resize={true}
          filter={true}
          range={true}
          readonly={true}
          exporting={true}
          plugins={[ExportExcelPlugin]}
        />
            </div>
          </div>
  )
}

// Grid component for Stacked View (grouped by prefix)
const StackedGroupGrid = ({ results, fields, formatFieldName }) => {
  const gridRef = useRef(null)

  const columns = useMemo(() => {
    if (!fields || fields.length === 0) {
      return []
    }
    
    return [
      {
        prop: 'row_number',
        name: '#',
        size: 60,
        sortable: true
      },
      ...fields.map(field => ({
        prop: field,
        name: formatFieldName(field),
        size: 200,
        sortable: true,
        cellTemplate: (h, { model }) => {
          const value = model[field] || '-'
          
          if (field === 'telephone' && value !== '-') {
            return h('a', {
              href: `tel:${value}`,
              class: 'text-primary-600 hover:text-primary-800 font-medium',
              innerHTML: value
            })
          }
          if (field === 'email' && value !== '-') {
            return h('a', {
              href: `mailto:${value}`,
              class: 'text-primary-600 hover:text-primary-800 font-medium',
              innerHTML: value
            })
          }
          if ((field === 'website' || field.includes('url')) && value !== '-') {
            return h('a', {
              href: value,
              target: '_blank',
              rel: 'noopener noreferrer',
              class: 'text-blue-600 hover:underline',
              innerHTML: '<i class="fa-solid fa-link"></i>'
            })
          }
          
          return h('span', {
            class: 'text-dark-300',
            innerHTML: value
          })
        }
      })),
      {
        prop: 'source_url',
        name: 'URL',
        size: 100,
        sortable: true,
        cellTemplate: (h, { model }) => {
          return h('a', {
            href: model.url,
            target: '_blank',
            rel: 'noopener noreferrer',
            class: 'text-primary-600 hover:underline',
            innerHTML: '<i class="fa-solid fa-link"></i>'
          })
        }
      }
    ]
  }, [fields, formatFieldName])

  const source = useMemo(() => {
    if (!results || results.length === 0) {
      return []
    }
    
    return results.map((result, idx) => ({
      row_number: idx + 1,
      url: result.url,
      source_url: result.url,
      ...result.extracted_data
    }))
  }, [results])

  if (!columns || columns.length === 0 || !source || source.length === 0) {
    return (
      <div className="flex items-center justify-center h-full bg-light-100 rounded-lg p-4">
        <p className="text-sm text-dark-100">No data to display</p>
      </div>
    )
  }

  return (
    <RevoGrid 
      ref={gridRef}
      columns={columns} 
      source={source}
      theme="compact"
      resize={true}
      filter={true}
      range={true}
      readonly={true}
    />
  )
}

// Grid component for Session Results view
const SessionResultsGrid = ({ results }) => {
  const gridRef = useRef(null)

  const handleExportCSV = async () => {
    if (!gridRef.current) return
    
    try {
      const plugins = await gridRef.current.getPlugins()
      const exportPlugin = plugins.find(p => p.exportFile)
      
      if (exportPlugin) {
        await exportPlugin.exportFile({ 
          filename: `data_session_results_${Date.now()}`,
          mime: 'text/csv',
          fileKind: 'csv'
        })
        toast.success('CSV export successful!')
      }
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Export failed')
    }
  }

  const handleExportExcel = async () => {
    if (!gridRef.current) return
    
    try {
      const plugins = await gridRef.current.getPlugins()
      const exportPlugin = plugins.find(p => p instanceof ExportExcelPlugin)
      
      if (exportPlugin) {
        const exportConfig = {
          sheetName: `data_session_results_${Date.now()}.xlsx`,
          writingOptions: {
            type: 'file',
            bookType: 'xlsx',
            compression: true,
            ignoreEC: true
          }
        }
        await exportPlugin.export(exportConfig)
        toast.success('Excel export successful!')
      } else {
        toast.error('Excel export plugin not available')
      }
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Export failed')
    }
  }

  const getAllFields = (results) => {
    const fields = new Set()
    results.forEach(result => {
      if (result.extracted_data) {
        Object.keys(result.extracted_data).forEach(key => {
          if (!['source_url', 'date_collected', 'date_entered', 'status'].includes(key)) {
            fields.add(key)
          }
        })
      }
    })
    return Array.from(fields)
  }

  const formatFieldName = (fieldName) => {
    return fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const columns = useMemo(() => {
    const fields = getAllFields(results)
    
    return [
      {
        prop: 'status',
        name: 'Status',
        size: 100,
        sortable: true,
        cellTemplate: (h, { model }) => {
          const status = model.success
          return h('div', {
            class: 'flex items-center justify-center',
            innerHTML: status 
              ? '<i class="fa-solid fa-check-circle text-green-600"></i>' 
              : '<i class="fa-solid fa-times-circle text-red-600"></i>'
          })
        }
      },
      {
        prop: 'source_url',
        name: 'Source URL',
        size: 250,
        sortable: true,
        cellTemplate: (h, { model }) => {
          return h('a', {
            href: model.url,
            target: '_blank',
            rel: 'noopener noreferrer',
            class: 'text-primary-600 hover:underline text-xs truncate',
            innerHTML: model.url
          })
        }
      },
      ...fields.map(field => ({
        prop: field,
        name: formatFieldName(field),
        size: 200,
        sortable: true,
        cellTemplate: (h, { model }) => {
          const value = model[field] || '-'
          
          if (field === 'telephone' && value !== '-') {
            return h('a', {
              href: `tel:${value}`,
              class: 'text-primary-600 hover:text-primary-800 font-medium',
              innerHTML: value
            })
          }
          if (field === 'email' && value !== '-') {
            return h('a', {
              href: `mailto:${value}`,
              class: 'text-primary-600 hover:text-primary-800 font-medium',
              innerHTML: value
            })
          }
          if ((field === 'website' || field.includes('url')) && value !== '-') {
            return h('a', {
              href: value,
              target: '_blank',
              rel: 'noopener noreferrer',
              class: 'text-blue-600 hover:underline',
              innerHTML: '<i class="fa-solid fa-link"></i>'
            })
          }
          
          return h('span', {
            class: 'text-dark-300',
            innerHTML: value
          })
        }
      }))
    ]
  }, [results])

  const source = useMemo(() => {
    return results.map(result => ({
      success: result.success,
      url: result.url,
      source_url: result.url,
      ...result.extracted_data
    }))
  }, [results])

  const handleExportJSON = () => {
    const json = JSON.stringify(results.map(r => r.extracted_data), null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `data_session_${Date.now()}.json`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      <div className="mb-4 flex gap-3">
        <button
          onClick={handleExportCSV}
          className="px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-lg font-semibold transition-all shadow-md hover:shadow-lg flex items-center"
        >
          <i className="fa-solid fa-file-csv mr-2"></i>
          Export CSV
        </button>
        <button
          onClick={handleExportExcel}
          className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg font-semibold transition-all shadow-md hover:shadow-lg flex items-center"
        >
          <i className="fa-solid fa-file-excel mr-2"></i>
          Export Excel
        </button>
        <button
          onClick={handleExportJSON}
          className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg font-semibold transition-all shadow-md hover:shadow-lg flex items-center"
        >
          <i className="fa-solid fa-file-code mr-2"></i>
          Export JSON
        </button>
        </div>
      <div style={{ height: '60vh' }}>
        <RevoGrid 
          ref={gridRef}
          columns={columns} 
          source={source}
          theme="compact"
          resize={true}
          filter={true}
          range={true}
          readonly={true}
          exporting={true}
          plugins={[ExportExcelPlugin]}
        />
      </div>
    </div>
  )
}

export default DataHistory
